import React from 'react'
import { FormattedMessage } from 'react-intl'

import { DatePickerModeEnum } from './date-picker.enum'
import { SearchBarPicker } from '../../atoms'
import { ISearchBarPicker } from '../../atoms/fields/search-bar-picker/search-bar-picker'
import { SearchBarPickerModeEnum } from '../../atoms/fields/search-bar-picker/search-bar-picker.enum'
import Icon from '../../atoms/icons/icon'
import { useDateFormatter } from '../../../lib/hooks'
import { cn } from '../../../shadcn/utils'
import { capitalizeFirstLetter } from '../../../utils'

export interface DateSearchBarPickerProps
  extends Omit<ISearchBarPicker, 'value' | 'placeholder' | 'label' | 'mode'> {
  className?: string
  inputStyle?: React.CSSProperties
  isDatesSelected: boolean
  allowMultiDates: boolean
  hideLabels?: boolean
  revampPicker: boolean
  fieldValue?: { startDate?: Date; endDate?: Date; flexible?: boolean }
  isCorporateEveningEvent: boolean
  mode: DatePickerModeEnum
}

const getLabel = (
  allowMultiDates: boolean,
  hideLabels?: boolean,
): React.ReactNode => {
  if (hideLabels) {
    return ''
  }

  return allowMultiDates ? (
    <FormattedMessage id='ui.DatePicker.arrival' defaultMessage='Arrivée' />
  ) : (
    <FormattedMessage id='ui.DatePicker.date' defaultMessage='Date' />
  )
}

const getPlaceholder = (
  allowMultiDates: boolean,
  isCorporateEveningEvent: boolean,
  revampPicker: boolean,
): React.ReactNode => {
  if (revampPicker) {
    return (
      <FormattedMessage
        id='ui.DatesAdultsPicker.yourDates'
        defaultMessage='Vos dates'
      />
    )
  }
  if (allowMultiDates) {
    return (
      <FormattedMessage id='ui.DatePicker.from' defaultMessage='De quand ?' />
    )
  }
  return isCorporateEveningEvent ? (
    <FormattedMessage
      id='ui.DatePicker.corporateEveningEventFrom'
      defaultMessage='A quelle date ?'
    />
  ) : (
    <FormattedMessage
      id='ui.DatePicker.bookingDate'
      defaultMessage='Quand partez-vous ?'
    />
  )
}

const formatFieldValue = (
  fieldValue: { startDate?: Date; endDate?: Date; flexible?: boolean } = {},
  dateFormatter: ReturnType<typeof useDateFormatter>,
  revampPicker: boolean,
): string => {
  if (!fieldValue?.startDate) {
    return ''
  }

  if (revampPicker) {
    const { startDate, endDate, flexible } = fieldValue
    if (!endDate) {
      return dateFormatter.format({
        date: startDate,
        formatString: 'dayMonth',
        useUTC: true,
      })
    }
    if (flexible) {
      return capitalizeFirstLetter(
        dateFormatter.format({
          date: startDate,
          formatString: 'MonthYear',
          useUTC: true,
        }),
      )
    }
    return dateFormatter.formatDateRange({
      startDate,
      endDate,
      formatString: 'dayMonthLong',
      useUTC: true,
    })
  }

  return dateFormatter.format({
    date: fieldValue.startDate,
    formatString: 'dayMonth',
    useUTC: true,
  })
}

export const DateSearchBarPicker: React.FC<DateSearchBarPickerProps> = ({
  className,
  inputStyle,
  isDatesSelected,
  allowMultiDates,
  hideLabels,
  revampPicker,
  fieldValue,
  isCorporateEveningEvent,
  mode,
  ...props
}) => {
  const dateFormatter = useDateFormatter()

  const searchBarPickerProps = {
    ...props,
    label: getLabel(allowMultiDates, hideLabels),
    placeholder: getPlaceholder(
      allowMultiDates,
      isCorporateEveningEvent,
      revampPicker,
    ),
    value: formatFieldValue(fieldValue, dateFormatter, revampPicker),
    mode:
      mode === DatePickerModeEnum.HOUSE_SEARCH
        ? SearchBarPickerModeEnum.HOUSE_SEARCH
        : undefined,
  }

  const hasValues = !!fieldValue?.startDate && !!fieldValue?.endDate
  const hideIcon =
    (revampPicker && hasValues) || mode !== DatePickerModeEnum.HOUSE_SEARCH

  return (
    <div className={className} style={inputStyle}>
      {hideIcon ? null : (
        <div
          className={cn(
            isDatesSelected || !!fieldValue?.startDate
              ? 'text-black'
              : 'text-grey-600',
          )}
        >
          <Icon name='calendar' size={15} />
        </div>
      )}
      <SearchBarPicker {...searchBarPickerProps} />
    </div>
  )
}
